import {
	UserGroupPermission,
	UserGroupPermissionsModel
} from "client/Api/UserGroupsManagement/Permissions/userGroupPermissions";
import {
	ExpandableTitlebar,
	FormItem,
	HeightAnimationContainer,
	SegmentedControl,
	SegmentedControlItem,
	Select,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import styles from "./UserGroupPermissionsSection.module.less";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import classNames from "classnames";

export type UserGroupPermissionsSectionProps = {
	attachable: IValueInputAttachable<UserGroupPermissionsModel>;
};

export const UserGroupPermissionsSection = (props: UserGroupPermissionsSectionProps): ReactElement => {
	const {
		attachable
	} = props;

	const [expandedId, setExpandedId] = useState<SectionId | undefined>("adminsettings");

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const toggleHandler = useCallback(
		(id: SectionId): void => {
			setExpandedId(
				(x): SectionId | undefined => {
					if (x === id) {
						return undefined;
					}

					return id;
				}
			);
		},
		[]
	);

	return (
		<div
			{...setTrackingId("user-group-permissions")}
		>
			<ul className={styles.list}>
				{sections.map((x): ReactElement => (
					<li key={x.id}>
						<Section
							attachable={attachable}
							configuration={x}
							onToggle={toggleHandler}
							isExpanded={x.id === expandedId}
							isMobile={isMobile}
						/>
					</li>
				))}
			</ul>
		</div>
	);
};
UserGroupPermissionsSection.displayName = "UserGroupPermissionsSection";

type SectionProps = {
	attachable: IValueInputAttachable<UserGroupPermissionsModel>;
	configuration: PermissionSectionConfiguration;
	isExpanded: boolean;
	onToggle: (id: SectionId) => void;
	isMobile: boolean;
};

const Section = (props: SectionProps): ReactElement => {
	const {
		attachable,
		configuration,
		isExpanded,
		onToggle,
		isMobile
	} = props;

	const {
		id,
		titles,
		items
	} = configuration;

	const toggleHandler = useCallback(
		(): void => {
			onToggle(id);
		},
		[id, onToggle]
	);

	const title = useMemo(
		(): string => titles.map(localize)
			.join(" / "),
		[titles]
	);

	return (
		<div
			{...setTrackingId(id)}
			className={styles.section}
		>
			<ExpandableTitlebar
				text={title}
				isExpanded={isExpanded}
				onToggle={toggleHandler}
				trackingId="title"
			/>

			<HeightAnimationContainer isVisible={isExpanded}>
				<ul
					className={classNames(styles.list, styles.list_permissionsTopLevel)}
				>
					{items.map(
						(x, i): ReactElement | null => {
							if (isGroup(x)) {
								return (
									<li key={`group_${i}`}>
										<PermissionGroup
											attachable={attachable}
											group={x}
											isMobile={isMobile}
										/>
									</li>
								);
							}

							return (
								<li key={x.property}>
									<Permission
										attachable={attachable}
										configuration={x}
										isMobile={isMobile}
									/>
								</li>
							);
						})
					}
				</ul>
			</HeightAnimationContainer>
		</div>
	);
};
Section.displayName = "Section";

type PermissionGroupProps = {
	attachable: IValueInputAttachable<UserGroupPermissionsModel>;
	group: PermissionConfigurationGroup;
	isMobile: boolean;
};

const PermissionGroup = (props: PermissionGroupProps): ReactElement => {
	const {
		attachable,
		group,
		isMobile
	} = props;

	return (
		<>
			<h4 className={styles.groupTitle}>{localize(group.title)}</h4>

			<ul className={styles.list}>
				{group.permissions
					.map((x): ReactElement => (
						<li key={x.property}>
							<Permission
								attachable={attachable}
								configuration={x}
								isMobile={isMobile}
							/>
						</li>
					))}
			</ul>
		</>
	);
};
PermissionGroup.displayName = "PermissionGroup";

type PermissionProps = {
	attachable: IValueInputAttachable<UserGroupPermissionsModel>;
	configuration: PermissionConfiguration;
	isMobile: boolean;
};

const Permission = (props: PermissionProps): ReactElement => {
	const {
		attachable,
		configuration,
		isMobile
	} = props;

	const {
		title,
		description,
		property
	} = configuration;

	const propertyAttachable: IValueInputAttachable<UserGroupPermission> = attachable.for(property);

	return (
		<>
			<FormItem
				hasNoMargin
			>
				<div className={styles.permission}>
					<div className={styles.titleAndDescription}>
						<span className={styles.title}>
							{localize(title)}
						</span>
						<span className={styles.description}>
							{localize(description)}
						</span>
					</div>

					<div className={styles.permissionOptions}>
						{isMobile
							? (
								<Select
									{...propertyAttachable.attachToSelect()}
									options={permissionOptions}
									menuTitle={localize(title)}
								/>
							)
							: (
								<SegmentedControl
									{...propertyAttachable.attachToRadioButtonGroup()}
									options={permissionOptions}
								/>
							)
						}

						<ValidationError {...propertyAttachable.attachToValidationError()} />
					</div>
				</div>
			</FormItem>
		</>
	);
};
Permission.displayName = "Permission";

const permissionOptions: readonly SegmentedControlItem<UserGroupPermission>[] = [
	{ text: localize("_Deny"), value: UserGroupPermission.Denied, activeColor: "danger" },
	{ text: localize("_NotSet"), value: UserGroupPermission.NotSet },
	{ text: localize("_Allow"), value: UserGroupPermission.Allowed, activeColor: "success" }
];

type SectionId = "adminsettings" | "app" | "hub";

type PermissionSectionConfiguration = {
	id: SectionId;
	titles: readonly LocalizationKey[];
	items: readonly PermissionConfigurationRootItem[];
};

type PermissionConfiguration = {
	title: LocalizationKey;
	description: LocalizationKey;
	property: keyof UserGroupPermissionsModel;
};

type PermissionConfigurationGroup = {
	title: LocalizationKey;
	permissions: readonly PermissionConfiguration[];
};

type PermissionConfigurationRootItem =
	| PermissionConfiguration
	| PermissionConfigurationGroup;

function isGroup(item: PermissionConfigurationRootItem): item is PermissionConfigurationGroup {
	return "permissions" in item
		&& Array.isArray(item.permissions);
}

const sections: readonly PermissionSectionConfiguration[] = [
	{
		id: "adminsettings",
		titles: ["_AdministratorSettings"],
		items: [
			{
				title: "_ManageCompanyInformationAndPricingPackages",
				description: "_CanManageBillingAndLicensesDescription",
				property: "canManageBillingModulesAndLicenses"
			},
			{
				title: "_ManageUsersGroupsAndUserProvisioning",
				description: "_CanManageUsersGroupsAndUserProvisioningDescription",
				property: "canManageUsersGroupsAndUserProvisioning"
			},
			{
				title: "_ManageBrandingSets",
				description: "_CanManageBrandingDescription",
				property: "canManageBranding"
			},
			{
				title: "_CreateAndManageIntegrationBuilderIntegrations",
				description: "_CanCreateAndManageIntegrationBuilderIntegrationsDescription",
				property: "canCreateAndManageIntegrationBuilderIntegrations"
			},
			{
				title: "_ViewAnalytics",
				description: "_CanViewAnalyticsDescription",
				property: "canViewAnalytics"
			}
		]
	},
	{
		id: "app",
		titles: ["_Apps", "_SharedTileGroups", "_Spaces"],
		items: [
			{
				title: "_Apps",
				permissions: [
					{
						title: "_CreateAppsInAppStore",
						description: "_CanCreateAppsDescription",
						property: "canCreateApps"
					},
					{
						title: "_ManageAllAppsInTheAppStore",
						description: "_CanManageAllAppsDescription",
						property: "canManageAllApps"
					},
					{
						title: "_UploadAndManageAppIcons",
						description: "_CanUploadAndManageAppIconsDescription",
						property: "canUploadAndManageAppIcons"
					},
					{
						title: "_RequestAppsInTheAppStore",
						description: "_CanMakeUserRequestsDescription",
						property: "canMakeUserRequests"
					}
				]
			},
			{
				title: "_SharedTileGroups",
				permissions: [
					{
						title: "_CreateSharedTileGroups",
						description: "_CanCreateSharedGroupsDescription",
						property: "canCreateSharedGroups"
					},
					{
						title: "_ManageAllSharedTileGroups",
						description: "_CanManageSharedTileGroupsDescription",
						property: "canManageSharedTileGroups"
					}
				]
			},
			{
				title: "_Spaces",
				permissions: [
					{
						title: "_EditOwnWorkspace",
						description: "_CanEditOwnWorkspaceDescription",
						property: "canEditOwnWorkspace"
					},
					{
						title: "_CreateAndManageAllSpaces",
						description: "_CanCreateAndManageSpacesDescription",
						property: "canCreateAndManageSpaces"
					}
				]
			}
		]
	},
	{
		id: "hub",
		titles: ["_TheHub"],
		items: [
			{
				title: "_CreateAndManageAllAnnouncementsCategoriesAndGroups",
				description: "_CanCreateAnnouncementsAndCategoriesDescription",
				property: "canCreateAnnouncementsAndCategories"
			},
			{
				title: "_CreateAndManageAllCommunityPostsAndCommunities",
				description: "_CanCreateAndManageCommunitiesAndCommunityPostsDescription",
				property: "canCreateAndManageCommunitiesAndCommunityPosts"
			},
			{
				title: "_CreateAndManageAllEventsCategoriesAndGroups",
				description: "_CanCreateEventsAndCategoriesDescription",
				property: "canCreateEventsAndCategories"
			},
			{
				title: "_CreateAndManageAllKnowledgeArticlesCategoriesAndGroups",
				description: "_CanCreateKnowledgeItemsAndCategoriesDescription",
				property: "canCreateKnowledgeItemsAndCategories"
			},
			{
				title: "_CreateAndManageHubTemplates",
				description: "_CanCreateHubTemplatesDescription",
				property: "canCreateHubTemplates"
			},
			{
				title: "_SendDirectPopUps",
				description: "_CanSendDirectPopupsDescription",
				property: "canSendDirectPopups"
			}
		]
	}
];
